/**
 * <tv-review-carousel>: 3-up on desktop (middle card active), 1-up on mobile. Dots, arrows, autoplay.
 * Cards are server-rendered; the track is translated so the current card is centered (desktop) or shown (mobile).
 */
class TvReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tv-track]');
    this.cards = Array.from(this.querySelectorAll('[data-tv-review]'));
    this.dots = Array.from(this.querySelectorAll('[data-tv-dot]'));
    this.count = this.cards.length;
    this.index = 0;
    this.autoplay = this.dataset.autoplay === 'true' && this.count > 1;
    this.speed = Number(this.dataset.speed) || 5000;

    this.querySelector('[data-tv-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.stop(); });
    this.querySelector('[data-tv-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.stop(); });
    this.dots.forEach((d) => d.addEventListener('click', () => { this.go(Number(d.dataset.index)); this.stop(); }));

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());

    // Swipe on touch
    let startX = null;
    this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    this.track.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) { this.go(this.index + (dx < 0 ? 1 : -1)); this.stop(); }
      startX = null;
    }, { passive: true });

    this.onResize = () => this.render();
    window.addEventListener('resize', this.onResize);

    this.render();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
  }

  get perView() {
    return window.innerWidth >= 750 ? 3 : 1;
  }

  go(i) {
    if (this.count < 2) return;
    this.index = (i + this.count) % this.count;
    this.render();
  }

  render() {
    if (!this.track || !this.count) return;
    const first = this.cards[0];
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const cardW = first.getBoundingClientRect().width;
    const step = cardW + gap;
    const perView = this.perView;

    if (perView === 1 || this.count <= perView) {
      // Simple slide (mobile), or everything fits: no translation needed when all fit.
      const offset = this.count <= perView && perView > 1 ? 0 : this.index * step;
      this.track.style.transform = `translateX(${-offset}px)`;
      this.cards.forEach((c) => c.classList.remove('is-dimmed'));
    } else {
      // Desktop: current card in the middle, neighbours dimmed. Wrap by reordering flex items.
      const prev = (this.index - 1 + this.count) % this.count;
      const next = (this.index + 1) % this.count;
      this.cards.forEach((c, i) => {
        let order = 10 + i; // parked off-screen to the right
        if (i === prev) order = 0;
        else if (i === this.index) order = 1;
        else if (i === next) order = 2;
        c.style.order = String(order);
        c.classList.toggle('is-dimmed', i !== this.index);
      });
      this.track.style.transform = 'translateX(0)';
    }

    this.dots.forEach((d, i) => {
      const on = i === this.index;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  start() {
    if (!this.autoplay || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }
}

if (!customElements.get('tv-review-carousel')) customElements.define('tv-review-carousel', TvReviewCarousel);
